'use client';

import { formatTimestampDate } from '@/lib/date-utils';
import { CalendarDays, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { PostEvent } from '@kit/community';
import { eventPath } from '@kit/shared/handles';
import { Badge } from '@kit/ui/badge';
import { cn } from '@kit/ui/cn';
import { Trans } from '@kit/ui/trans';

import { closesInDays, URGENCY_WINDOW_DAYS } from './post-attachments.urgency';

// ---------------------------------------------------------------------------
// Community event embed
// ---------------------------------------------------------------------------

export function eventHref(event: PostEvent): string {
  if (!event.slug) return '#';
  return eventPath(event.slug);
}

/** A community event is "live" (linkable, open deadline) when published-ish. */
function isEventClosed(event: PostEvent, days: number | null): boolean {
  return (
    event.status === 'closed' ||
    event.status === 'cancelled' ||
    (days !== null && days < 0)
  );
}

/**
 * Deadline chip/line for a community event — mirrors `AuditionDeadline`
 * but reads `applicationsToDate` and the community-event status vocabulary.
 * `overlay` renders glassy pills legible on the cover; `inline` renders
 * plain text for the compact chip card.
 */
export function EventDeadline({
  event,
  variant = 'inline',
  className,
}: {
  event: PostEvent;
  variant?: 'overlay' | 'inline';
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const days = closesInDays(event.applicationsToDate);
  const overlay = variant === 'overlay';

  if (isEventClosed(event, days)) {
    return (
      <Badge
        variant="secondary"
        className={cn(
          overlay &&
            'bg-black/55 text-white ring-1 ring-white/25 backdrop-blur-sm',
          className,
        )}
      >
        <Trans i18nKey="community:feed.auditionClosed" defaults="Closed" />
      </Badge>
    );
  }

  if (days === null) return null;

  if (days <= URGENCY_WINDOW_DAYS) {
    const label =
      days === 0
        ? t('community:feed.closesToday', 'Closes today')
        : t('community:feed.closesInDays', {
            count: days,
            defaultValue: 'Closes in {{count}} days',
          });
    return overlay ? (
      <Badge className={className}>{label}</Badge>
    ) : (
      <span className={cn('text-primary text-sm font-medium', className)}>
        {label}
      </span>
    );
  }

  const dateLabel = (
    <Trans
      i18nKey="community:feed.closesOn"
      defaults="Closes {{date}}"
      values={{
        date: formatTimestampDate(
          event.applicationsToDate,
          { month: 'short', day: 'numeric', year: 'numeric' },
          i18n.language,
        ),
      }}
    />
  );

  return overlay ? (
    <Badge
      variant="secondary"
      className={cn(
        'bg-black/55 text-white ring-1 ring-white/25 backdrop-blur-sm',
        className,
      )}
    >
      {dateLabel}
    </Badge>
  ) : (
    <span className={cn('text-muted-foreground text-sm', className)}>
      {dateLabel}
    </span>
  );
}

/**
 * Human label for a community event's `event_type`. Known enum values map
 * to a translated label; anything unrecognized (or null) falls back to the
 * generic "Event" badge copy.
 */
export function EventTypeLabel({ eventType }: { eventType: string | null }) {
  const { t } = useTranslation();
  const fallback = t('community:feed.eventBadge', 'Event');
  if (!eventType) return <>{fallback}</>;
  return (
    <>
      {t(`community:feed.eventTypes.${eventType}`, { defaultValue: fallback })}
    </>
  );
}

export function EventTypeBadge({ event }: { event: PostEvent }) {
  return (
    <Badge
      variant="secondary"
      className="bg-background/80 text-foreground backdrop-blur-sm"
    >
      <CalendarDays className="mr-1 h-3 w-3" aria-hidden="true" />
      <EventTypeLabel eventType={event.eventType} />
    </Badge>
  );
}

export function EventTitle({ event }: { event: PostEvent }) {
  return (
    <>
      {event.title ?? (
        <Trans
          i18nKey="community:feed.untitledEvent"
          defaults="Untitled event"
        />
      )}
    </>
  );
}

/** Concise date span: "Jul 15 – Aug 2, 2024" (or a single date). */
function formatEventDates(
  start: string | null,
  end: string | null,
): string | null {
  if (!start) return null;
  const from = formatTimestampDate(start);
  if (!end || end === start) return from;
  const to = formatTimestampDate(end);
  return from && to ? `${from} – ${to}` : (from ?? to);
}

/**
 * The two facts that matter most for a dance event: WHEN (its date span, from
 * community_event_dates — distinct from the application deadline) and WHERE
 * (city, country). Rendered over the cover's bottom gradient, white on the
 * dark scrim so it stays legible on any photo. Each fact is omitted when
 * absent, so the row never shows an empty icon.
 */
export function EventMeta({
  event,
  className,
}: {
  event: PostEvent;
  className?: string;
}) {
  const dates = formatEventDates(event.startDate, event.endDate);
  const location = [event.city, event.country].filter(Boolean).join(', ');
  if (!dates && !location) return null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-white/90',
        className,
      )}
    >
      {dates ? (
        <span className="inline-flex items-center gap-1">
          <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
          {dates}
        </span>
      ) : null}
      {location ? (
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
          {location}
        </span>
      ) : null}
    </div>
  );
}
